import dayjs, { Dayjs } from "dayjs";
import {
  MemberFinanceInformationDto,
  WWFeeInformationDto,
  DirectDebitNotificationFailureReason,
  DirectDebitNotificationResultMember,
} from "../types/membersTypes";
import { formatDate } from "./dateUtils";

/** Minimum notice period (in days) before a SEPA direct debit may be executed. */
export const DIRECT_DEBIT_NOTIFICATION_PERIOD_DAYS = 14;

/**
 * Returns the earliest allowed Ausführungsdatum (today + 14 days, start of day).
 */
export const getMinDirectDebitExecutionDate = (): Dayjs =>
  dayjs().startOf("day").add(DIRECT_DEBIT_NOTIFICATION_PERIOD_DAYS, "day");

/**
 * Validates an execution date against the 14-day notification rules.
 * @returns A German error message, or null if the date is valid.
 */
export const validateDirectDebitExecutionDate = (date: Dayjs): string | null => {
  if (!date.isValid()) {
    return "Bitte gib ein gültiges Ausführungsdatum ein";
  }

  const executionDay = date.startOf("day");

  const minExecutionDate = getMinDirectDebitExecutionDate();
  if (executionDay.isBefore(minExecutionDate)) {
    return "Das Ausführungsdatum muss mindestens 14 Tage in der Zukunft liegen.";
  }

  return null;
};

/**
 * Validates the format of an IBAN (International Bank Account Number).
 * @param iban - The IBAN string to validate
 * @returns true if the IBAN is valid, false otherwise
 */
export const checkIban = (iban: string): boolean => {
  // Normalize IBAN (remove spaces and set uppercase) and validate basic format
  const normalizedIban = iban.replace(/\s+/g, "").toUpperCase();

  // Checks if the IBAN starts with 2 letters (country code), followed by 2 digits (check digits), and then 11 to 30 alphanumeric characters (BBAN)
  const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

  return ibanRegex.test(normalizedIban);
};

/**
 * Validates the format of a BIC (Bank Identifier Code).
 * @param bic - The BIC string to validate
 * @returns true if the BIC is valid, false otherwise
 */
export const checkBic = (bic: string): boolean => {
  // Normalize BIC (uppercase) and validate 8 or 11 characters (letters + digits)
  const normalizedBic = bic.toUpperCase();

  // BIC must be either 8 or 11 characters long, consisting of uppercase letters and digits
  const bicRegex = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/;

  return bicRegex.test(normalizedBic);
};

interface GetFilteredAndSortedMembersParams {
  memberFinanceInformation: MemberFinanceInformationDto[] | WWFeeInformationDto[];
  statusFilter: string;
  searchFilter: string;
  accountFilter?: string;
  sortOption: string;
  nameSort: string;
}

/**
 * Filters and sorts member finance information without mutating the original array.
 */
export const getFilteredAndSortedMembers = ({
  memberFinanceInformation,
  statusFilter,
  searchFilter,
  accountFilter,
  sortOption,
  nameSort,
}: GetFilteredAndSortedMembersParams): MemberFinanceInformationDto[] => {
  let filteredMembers = [...memberFinanceInformation];

  // Filters by status
  if (statusFilter !== "") {
    filteredMembers = filteredMembers.filter((member) => {
      return member.memberStatus.toString() === statusFilter;
    });
  }

  // Filters by account
  if (accountFilter !== "allMembers") {
    if (accountFilter === "hasAccount") {
      filteredMembers = filteredMembers.filter((member) => {
        return (
          member.iban !== null && member.bic !== null && member.iban.toString() !== "" && member.bic.toString() !== ""
        );
      });
    }

    if (accountFilter === "noAccount") {
      filteredMembers = filteredMembers.filter((member) => {
        return (
          member.iban === null || member.bic === null || member.iban.toString() === "" || member.bic.toString() === ""
        );
      });
    }
  }

  // Filters by search input
  const lowerCaseSearchFilter = searchFilter.toLowerCase();

  filteredMembers = filteredMembers.filter((member) => {
    return (
      member.firstName.toLowerCase().includes(lowerCaseSearchFilter) ||
      member.lastName.toLowerCase().includes(lowerCaseSearchFilter)
    );
  });

  // Create a copy before sorting because sort() mutates the array in place
  const sortedMembers = [...filteredMembers];

  // Sorts by last changed in ascending order
  if (sortOption === "lastchange ASC") {
    sortedMembers.sort((a, b) => {
      if (a.lastchange === null && b.lastchange === null) return 0;
      if (a.lastchange === null) return -1;
      if (b.lastchange === null) return 1;

      const dateA = new Date(a.lastchange);
      const dateB = new Date(b.lastchange);

      return dateA.getTime() - dateB.getTime();
    });

    // Sorts by last changed in descending order
  } else if (sortOption === "lastchange DESC") {
    sortedMembers.sort((a, b) => {
      if (a.lastchange === null && b.lastchange === null) return 0;
      if (a.lastchange === null) return 1;
      if (b.lastchange === null) return -1;

      const dateA = new Date(a.lastchange);
      const dateB = new Date(b.lastchange);

      return dateB.getTime() - dateA.getTime();
    });
  }

  // Sorts by lastname in ascending alphabetical order
  if (nameSort === "up") {
    sortedMembers.sort((a, b) => {
      return a.lastName.localeCompare(b.lastName);
    });

    // Sorts by lastname in descending alphabetical order
  } else if (nameSort === "down") {
    sortedMembers.sort((a, b) => {
      return b.lastName.localeCompare(a.lastName);
    });
  }

  return sortedMembers;
};

type FeeCsvExportMember = Pick<MemberFinanceInformationDto, "memberId" | "accountHolder" | "iban" | "traineeSince">;

/**
 * Builds SEPA-oriented CSV export rows for fee pages.
 */
export const buildFeeCsvExportData = (
  members: FeeCsvExportMember[],
  paymentReason: string,
  getAmount: (member: FeeCsvExportMember) => string
) =>
  members.map((member) => ({
    Kontoinhaber: member.accountHolder ?? "",
    IBAN: member.iban ?? "",
    "Mandatsreferenz-Nr.": `JBT-${member.memberId}`,
    Unterschriftsdatum: formatDate(member.traineeSince),
    Verwendungszweck: paymentReason,
    Betrag: getAmount(member),
  }));

/**
 * Builds SEPA-oriented CSV export rows for members listed in a notification report.
 * Uses client-side member finance data as the primary source, with report firstName/lastName
 * as a fallback for Kontoinhaber when accountHolder is missing.
 */
export const buildFeeCsvExportDataForReportMembers = (
  members: FeeCsvExportMember[],
  reportMembers: DirectDebitNotificationResultMember[],
  paymentReason: string,
  getAmountByMemberId: (memberId: number) => string
) => {
  const membersById = new Map(members.map((member) => [Number(member.memberId), member]));

  const exportMembers = reportMembers.map((reportMember) => {
    const memberId = Number(reportMember.memberId);
    const clientMember = membersById.get(memberId);
    const reportName = [reportMember.firstName, reportMember.lastName].filter(Boolean).join(" ").trim();

    return {
      memberId,
      accountHolder: clientMember?.accountHolder ?? (reportName || null),
      iban: clientMember?.iban ?? null,
      traineeSince: clientMember?.traineeSince ?? null,
    };
  });

  return buildFeeCsvExportData(exportMembers, paymentReason, (member) => getAmountByMemberId(member.memberId));
};

const DIRECT_DEBIT_FAILURE_REASON_LABELS: Record<DirectDebitNotificationFailureReason, string> = {
  [DirectDebitNotificationFailureReason.MEMBER_NOT_FOUND]: "Mitglied nicht gefunden",
  [DirectDebitNotificationFailureReason.NO_JBT_EMAIL]: "Keine JBT-E-Mail hinterlegt",
  [DirectDebitNotificationFailureReason.DECRYPTION_FAILED]: "Finanzdaten konnten nicht entschlüsselt werden",
  [DirectDebitNotificationFailureReason.NO_IBAN]: "Keine IBAN hinterlegt",
  [DirectDebitNotificationFailureReason.INVALID_IBAN]: "Ungültige IBAN",
  [DirectDebitNotificationFailureReason.MAIL_SEND_FAILED]: "E-Mail konnte nicht versendet werden",
};

/**
 * Returns a German label for a direct debit notification failure reason.
 */
export const getDirectDebitFailureReasonLabel = (reason?: DirectDebitNotificationFailureReason): string => {
  if (!reason) {
    return "Unbekannter Fehler";
  }
  return DIRECT_DEBIT_FAILURE_REASON_LABELS[reason] ?? "Unbekannter Fehler";
};
